import {
  all,
  call,
  CallEffect,
  put,
  PutEffect,
  select,
  SelectEffect,
  takeLatest,
} from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import client from '../../service/graphql';

import {
  ADD_NEW_ACTIVITY,
  CHECK_ACTIVITY_EXISTENCE,
  EDIT_ACTIVITY,
  GET_ACTIVITY_FOR_CLIENT_BY_ID,
  GET_MAGIC_SUGGESTIONS,
  GET_TOTAL_ACTIVITIES_COUNT_FOR_CLIENT,
} from '../../service/graphql/activityQuery';
import {
  activityActivated,
  activityDeactivated,
  addActivityFailure,
  addActivityRequest,
  addActivitySuccess,
  checkActivityExistence,
  checkActivityExistenceFailure,
  checkActivityExistenceSuccess,
  fetchActivitiesFailure,
  fetchActivitiesRequest,
  fetchActivitiesSuccess,
  fetchMagicSuggestionsFailure,
  fetchMagicSuggestionsRequest,
  fetchMagicSuggestionsSuccess,
  fetchTotalActivitiesCountFailure,
  fetchTotalActivitiesCountRequest,
  fetchTotalActivitiesCountSuccess,
  IActivity,
  IAddActivityPayload,
  IFetchActivitiesPayload,
  IMagicSuggestion,
  IStepMediaAsset,
  IUpdateActivityPayload,
  updateActivityFailure,
  updateActivityRequest,
  updateActivitySuccess,
} from './activitySlice';
import { IGraphQLResponse } from '../../types/commonTypes';
import { toastStyles } from '../../utils/utils';
import i18n from '../../i18n';
import { saveActivityStepMediaInS3 } from '../activityLibrary/activityLibrarySaga';

interface IActivityStepResponse {
  id: string;
  activityId: string;
  description: string;
  stepOrder: number;
  text: string;
  asset: string;
  assetType: string;
  createdAt: string;
  updatedAt: string;
  link: string;
}

interface IUserActivityResponse {
  id: string;
  activityId: string;
  currentStep: number;
  expiredAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface IGetActivityResponse {
  getActivitiesForClient: {
    totalActivityCount: string;
    isActivityCreated: boolean;
    activities: IActivity[];
  };
}

interface IGetActivityTotalCountResponse {
  getActivitiesForClient: {
    totalActivityCount: number;
  };
}

interface Suggestions {
  activities: IMagicSuggestion[];
}

interface IGetMagicSuggestionsResponse {
  getMagicSuggestions: {
    suggestions: Suggestions;
    is_completed: boolean;
    id: string;
  };
}

export interface IAddActivityResponse {
  activity: IActivity;
  activityStep: IActivityStepResponse[];
  userActivity: IUserActivityResponse;
}

export interface IAddActivityGraphQLResponse {
  addActivity: IAddActivityResponse;
}

interface IEditActivityGraphQLResponse {
  editActivity: IActivity;
}

export interface IGetPreSignedUrlResponse {
  link: string;
  asset: string;
}

interface ICheckActivityExistenceResponse {
  checkActivityExistence: {
    success: boolean;
    message: string;
  };
}

export interface IFileObject {
  link: string;
  assetFile: File;
  asset: string;
}

function* watchFetchActivities({ payload }: PayloadAction<IFetchActivitiesPayload>) {
  try {
    const { clientId, coachId, skip, take, filterText, schedule } = payload;
    const result: IGraphQLResponse<IGetActivityResponse> = yield call(client.query.bind(client), {
      query: GET_ACTIVITY_FOR_CLIENT_BY_ID,
      variables: {
        clientId,
        coachId,
        skip,
        take,
        filterText,
        schedule,
      },
      fetchPolicy: 'network-only',
    });
    yield put(
      fetchActivitiesSuccess({
        activities: result.data.getActivitiesForClient.activities,
        totalActivityCount: Number(result.data.getActivitiesForClient.totalActivityCount),
        isActivityCreated: result.data.getActivitiesForClient.isActivityCreated,
      })
    );
    yield put(
      fetchTotalActivitiesCountSuccess(
        Number(result.data.getActivitiesForClient.totalActivityCount)
      )
    );
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchActivitiesFailure(error.message));
  }
}

function* saveActivityStepMediaInS3Saga(fileObject: IFileObject) {
  yield call(saveActivityStepMediaInS3, { fileObject });
}

function* watchAddActivity({ payload }: PayloadAction<IAddActivityPayload>) {
  try {
    const { activityInput, stepMediaAssets, coachId } = payload;
    client.resetStore();
    const result: IGraphQLResponse<IAddActivityGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: ADD_NEW_ACTIVITY,
        variables: activityInput,
      }
    );

    const preSignedUrls: IGetPreSignedUrlResponse[] = result.data.addActivity.activityStep
      .filter((stepURL: IGetPreSignedUrlResponse) => stepURL.asset !== '')
      .map((stepURL: IGetPreSignedUrlResponse) => ({
        link: stepURL.link,
        asset: stepURL.asset,
      }));

    const fileWithPresignedUrls: IFileObject[] = stepMediaAssets
      .map((mediaAsset: IStepMediaAsset, index: number) =>
        mediaAsset.assetFile !== null
          ? {
              link: preSignedUrls[index].link,
              assetFile: mediaAsset.assetFile,
              asset: preSignedUrls[index].asset,
            }
          : null
      )
      .filter((fileObject): fileObject is IFileObject => fileObject !== null);

    yield all(
      fileWithPresignedUrls.map((fileObject) => call(saveActivityStepMediaInS3Saga, fileObject))
    );
    yield put(addActivitySuccess(result.data.addActivity.activity));
    if (result.data.addActivity.activity.clientId) {
      // after creating activity, fetch activities
      yield put(
        fetchActivitiesRequest({
          clientId: result.data.addActivity.activity.clientId,
          coachId,
          skip: 0,
          take: 10,
          filterText: '',
        })
      );
      toast.success(i18n.t('activity.activityTable.successToastMessage'), toastStyles.success);
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(addActivityFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchFetchMagicSuggestions({
  payload,
}: PayloadAction<{ clientId: string; coachId: string }>) {
  try {
    const result: IGraphQLResponse<IGetMagicSuggestionsResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_MAGIC_SUGGESTIONS,
        variables: payload,
        fetchPolicy: 'network-only',
      }
    );
    yield put(fetchMagicSuggestionsSuccess(result.data.getMagicSuggestions));
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchMagicSuggestionsFailure(error.message));
  }
}

function* watchUpdateActivity({
  payload,
}: PayloadAction<{ data: IUpdateActivityPayload; statusUpdate: boolean }>) {
  try {
    const result: IGraphQLResponse<IEditActivityGraphQLResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: EDIT_ACTIVITY,
        variables: {
          editActivityInput: payload.data,
        },
      }
    );
    const statusUpdateMsg = result.data.editActivity.isActive
      ? i18n.t('activity.assignActivity.activityActivated')
      : i18n.t('activity.assignActivity.activityDeactivated');
    const successMsg = payload.statusUpdate
      ? statusUpdateMsg
      : i18n.t('activity.assignActivity.activityUpdated');

    if (result.data.editActivity.isActive) {
      yield put(activityActivated());
    } else {
      yield put(activityDeactivated());
    }
    const tostStyle =
      payload.statusUpdate && !result.data.editActivity.isActive
        ? toastStyles.error
        : toastStyles.success;
    toast.success(successMsg, tostStyle);
    yield put(updateActivitySuccess(result.data.editActivity));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(updateActivityFailure(error.message));
      if (error.message === 'Forbidden resource') {
        toast.error(i18n.t('forbiddenResourceErrorMessage'), toastStyles.error);
      } else {
        toast.error(error.message, toastStyles.error);
      }
    }
  }
}

function* watchCheckActivityExistence({
  payload,
}: PayloadAction<string>): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  IGraphQLResponse<ICheckActivityExistenceResponse>
> {
  const selectedClientId = yield select((state) => state.client.selectedClient?.id);
  const currentCoachId = yield select((state) => state.coach.coach?.id);

  try {
    const result: IGraphQLResponse<ICheckActivityExistenceResponse> = yield call(
      client.query.bind(client),
      {
        query: CHECK_ACTIVITY_EXISTENCE,
        variables: {
          checkActivityExistenceArgs: {
            name: payload,
            clientId: selectedClientId,
            coachId: currentCoachId,
          },
        },
      }
    );

    yield put(checkActivityExistenceSuccess(result.data.checkActivityExistence.success));
  } catch (error) {
    if (error instanceof Error) yield put(checkActivityExistenceFailure(error.message));
  }
}

function* watchFetchTotalActivitiesCount({
  payload,
}: PayloadAction<{ clientId: string }>): Generator<
  CallEffect | PutEffect | SelectEffect,
  void,
  IGraphQLResponse<IGetActivityTotalCountResponse>
> {
  const currentCoachId = yield select((state) => state.coach.coach?.id);
  try {
    const result: IGraphQLResponse<IGetActivityTotalCountResponse> = yield call(
      client.query.bind(client),
      {
        query: GET_TOTAL_ACTIVITIES_COUNT_FOR_CLIENT,
        variables: {
          clientId: payload.clientId,
          coachId: currentCoachId,
        },
        fetchPolicy: 'cache-first',
      }
    );
    yield put(
      fetchTotalActivitiesCountSuccess(result.data.getActivitiesForClient.totalActivityCount)
    );
  } catch (error: unknown) {
    if (error instanceof Error) yield put(fetchTotalActivitiesCountFailure(error.message));
  }
}

export function* watchActivitySagas() {
  yield takeLatest(fetchActivitiesRequest, watchFetchActivities);
  yield takeLatest(addActivityRequest, watchAddActivity);
  yield takeLatest(fetchMagicSuggestionsRequest, watchFetchMagicSuggestions);
  yield takeLatest(updateActivityRequest, watchUpdateActivity);
  yield takeLatest(checkActivityExistence, watchCheckActivityExistence);
  yield takeLatest(fetchTotalActivitiesCountRequest, watchFetchTotalActivitiesCount);
}

const activitySagas = watchActivitySagas;
export default activitySagas;
